import { useSiteStore } from '~/stores/site'
import { usePromotionalStore } from '~/stores/promotional'
import { useConfigStore } from '~/stores/useConfigStore'
// import { devLog, devWarn, devError } from '~/utils/logger' // Commented out due to module not found
// import type { NuxtApp } from '#imports' // Type will be inferred or handled differently

export default defineNuxtPlugin({
  name: 'site-config',
  async setup(nuxtApp) { // Removed NuxtApp type annotation
    // Wrap existing logic in app:created hook
    (nuxtApp as any).hooks.hook('app:created', async () => {
      const siteStore = useSiteStore()
      const promotionalStore = usePromotionalStore()
      const configStore = useConfigStore()

      if (siteStore.isReady) {
        // devLog('[Plugin] Site configuration already initialized') // Commented out
        return
      }

      try {
        // devLog('[Plugin] Loading site configuration...') // Commented out
        
        const { data, error } = await useFetch('/api/site-config', {
          key: 'site-config',
          headers: {
            'Cache-Control': 'no-store, no-cache, must-revalidate, proxy-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            'Surrogate-Control': 'no-store'
          },
          transform: (response: any) => {
            return {
              name: response.name,
              description: response.description,
              logo: response.logo,
              phone: response.phone,
              salesPhone: response.salesPhone,
              servicePartsPhone: response.servicePartsPhone,
              showroom_address: response.showroom_address,
              map_embed: response.map_embed,
              map_directions: response.map_directions,
              timezone: response.timezone,
              departments: response.departments,
              team_members: response.team_members,
              sitelinks: response.sitelinks,
              promotional: response.promotional,
              finance: response.finance,
              price: response.price,
              payment_gateway: response.payment_gateway,
              deposit_amount: response.deposit_amount,
              forms: response.forms
            }
          }
        })

        if (error.value) {
          throw new Error(`API error: ${error.value.message}`)
        }

        if (!data.value) {
          throw new Error('Empty configuration received')
        }

        const siteConfig = data.value as any
        // devLog('[Plugin] Site configuration loaded successfully') // Commented out

        if (!siteConfig.name || !siteConfig.sitelinks) {
          // devWarn('[Plugin] Invalid configuration structure - missing name or sitelinks') // Commented out
        }

        if (!siteConfig.promotional || !Array.isArray(siteConfig.promotional)) {
          // devWarn('[Plugin] Missing or invalid promotional data, initializing with empty array') // Commented out
          siteConfig.promotional = [{ slides: [] }]
        } else if (siteConfig.promotional.length > 0 && !siteConfig.promotional[0].slides) {
          // devWarn('[Plugin] Promotional data missing slides property, initializing with empty array') // Commented out
          siteConfig.promotional[0].slides = []
        }

        siteStore.$patch(siteConfig)
        configStore.$patch({ config: siteConfig })
        
        if (siteConfig.promotional && Array.isArray(siteConfig.promotional)) {
          if (process.client) {
            if (document.readyState === 'complete') {
              promotionalStore.initializeFromData(siteConfig.promotional)
            } else {
              window.addEventListener('DOMContentLoaded', () => {
                promotionalStore.initializeFromData(siteConfig.promotional)
              })
            }
          } else {
            promotionalStore.initializeFromData(siteConfig.promotional)
          }
        }
        
        // Mark as initialized after patching data
        siteStore.isInitialized = true
        // devLog('[Plugin] Stores updated with configuration data') // Commented out
      } catch (error) {
        // devError('[Plugin] Failed to load site configuration:', error) // Commented out
        console.error('[Plugin] Critical error loading site configuration (devError commented out):', error) // Fallback console.error
        const fallbackConfig = {
          name: 'App',
          description: 'Welcome to our website',
          logo: '/logo.png',
          phone: '',
          showroom_address: '',
          lmct: '',
          sitelinks: {
            mainnav: [
              { name: 'Home', url: '/' },
              { name: 'About', url: '/about' },
              { name: 'Services', url: '/services' },
              { name: 'Parts', url: '/parts' },
              { name: 'Fleet', url: '/fleet' },
              { name: 'Contact', url: '/contact' }
            ],
            footer: []
          },
          promotional: [{ slides: [] }]
        }
        siteStore.$patch(fallbackConfig)
        configStore.$patch({ config: fallbackConfig })
        siteStore.isInitialized = true
      }
    })
  }
})
